import React, { useEffect, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { checkFilterItem, fetchFilterData } from "../../../../utils/fetchFilterData";
import userAuthenticationConfig from "../../../../utils/userAuthenticationConfig";
import Notification from "../../../elements/notification/Notification";
import TelegramManagersList from "./TelegramManagersList";
import TelegramManagersFilter from "./TelegramManagersFilter";
import FilterAppliedList from "../../../elements/filter/FilterAppliedList";
import TelegramAddManagerDialog from "./TelegramAddManagerDialog";
import TelegramEditManagerDialog from "./TelegramEditManagerDialog";
import TelegramManagerAttributesDialog from "./TelegramManagerAttributesDialog";
import DialogModal from "../../../elements/dialogModal/DialogModal";

const TelegramManagersContainer = () => {
  const { t } = useTranslation("telegramBotPage");
  const history = useHistory();

  const [filterData, setFilterData] = useState({
    "currentTab": checkFilterItem(history, "currentTab", "managers", false, true),
    "projectId": checkFilterItem(history, "projectId", null),
  });

  const [bots, setBots] = useState([]);
  const [botsLoading, setBotsLoading] = useState(false);
  const [managers, setManagers] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAttrs, setOpenAttrs] = useState(false);
  const [notification, setNotification] = useState({ visible: false, type: "", message: "" });

  const fetchBots = () => {
    if (botsLoading) return;
    setBotsLoading(true);
    axios.get("/api/telegram/bots", userAuthenticationConfig()).then(response => setBots(response.data || [])).catch(() => setBots([])).finally(() => setBotsLoading(false));
  };

  const fetchManagers = () => {
    let filterUrl = fetchFilterData(filterData);
    history.replace(filterUrl);
    setManagers(null);
    axios.get("/api/telegram/managers" + filterUrl, userAuthenticationConfig()).then(response => {
      if (response.status === 200) setManagers(response.data["hydra:member"] || []);
    }).catch(error => {
      setNotification({
        visible: true,
        type: "error",
        message: error?.response?.data?.message || t("managers.errors.load")
      });
      setManagers([]);
    });
  };

  useEffect(() => {
    fetchBots();
  }, []);

  useEffect(() => {
    fetchManagers();
  }, [filterData]);

  const handleToggled = (updatedManager) => {
    setManagers(prev => prev.map(m => m.id === updatedManager.id ? updatedManager : m));
  };

  const handleAdded = (targetProjectId) => {
    if (String(targetProjectId) !== String(filterData.projectId)) {
      setFilterData(prev => ({ ...prev, projectId: String(targetProjectId) }));
    } else {
      fetchManagers();
    }
  };

  const handleDeleteManager = () => {
    axios.delete(`/api/telegram/managers/${selectedManager.id}`, userAuthenticationConfig())
      .then(response => {
        if (response.status === 204) {
          setOpenDelete(false);
          setNotification({ visible: true, type: "success", message: t("managers.success.deleted") });
          fetchManagers();
        }
      })
      .catch(error => {
        setNotification({ visible: true, type: "error", message: error?.response?.data?.message || t("managers.errors.delete") });
      });
  };

  return (
    <>
      <Notification notification={notification} setNotification={setNotification} />
      <div className="content-head" style={{ justifyContent: "space-between" }}>
        <TelegramManagersFilter
          filterData={filterData}
          setFilterData={setFilterData}
          bots={bots}
          botsLoading={botsLoading}
          onOpen={fetchBots}
          t={t}
        />
        <TelegramAddManagerDialog
          bots={bots}
          hasBots={bots.length > 0}
          currentProjectId={filterData.projectId}
          setNotification={setNotification}
          onAdded={handleAdded}
          t={t}
        />
      </div>
      <FilterAppliedList
        filterData={filterData}
        setFilterData={setFilterData}
      />
      <TelegramManagersList
        managers={managers}
        onEdit={(m) => {
          setSelectedManager(m);
          setOpenEdit(true);
        }}
        onDelete={(m) => {
          setSelectedManager(m);
          setOpenDelete(true);
        }}
        onAttrs={(m) => {
          setSelectedManager(m);
          setOpenAttrs(true);
        }}
        onToggled={handleToggled}
        setNotification={setNotification}
        t={t}
      />
      {selectedManager && (
        <>
          <TelegramEditManagerDialog
            open={openEdit}
            onClose={() => setOpenEdit(false)}
            manager={selectedManager}
            setNotification={setNotification}
            onSaved={fetchManagers}
            t={t}
          />
          <DialogModal
            open={openDelete}
            setOpen={setOpenDelete}
            title={t("managers.dialog.deleteTitle")}
            content={t("managers.dialog.deleteText", { name: selectedManager?.name })}
            cancelButton={t("managers.dialog.cancel")}
            approveButton={t("managers.dialog.delete")}
            onApprove={handleDeleteManager}
          />
          <TelegramManagerAttributesDialog
            manager={selectedManager}
            projectId={selectedManager?.projectId}
            open={openAttrs}
            onClose={() => setOpenAttrs(false)}
            setNotification={setNotification}
          />
        </>
      )}
    </>
  );
};

export default TelegramManagersContainer;
